import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompanyService } from 'src/app/services/core/company/company.service';
import { BillService } from 'src/app/services/core/services/bills/bill.service';
import { VendorService } from 'src/app/services/core/services/vendors/vendor.service';
import { NUMBER_OF_RECORDS, BILL_PAYMENT_STAT_CARDS, BILL_AMOUNT_STAT_CARDS, getAmountPaid, getPaymentStatus, getPaymentStatusClass, getRemainingBalance } from 'src/app/utils/common.utils';
import { sweetAlert } from 'src/app/utils/swal';
import { Router, ActivatedRoute } from '@angular/router';
import { debounceTime, filter, Subject } from 'rxjs';
import * as moment from 'moment';

@UntilDestroy()

@Component({
  selector: 'app-bill-list',
  templateUrl: './bill-list.component.html',
  styleUrls: ['./bill-list.component.css']
})
export class BillListComponent implements OnInit {

  bills: any = [];
  filteredBills: any;
  totalPages = 0;
  totalResults = 0;
  search: any = '';
  private searchSubject = new Subject<string>();
  currentPage: number = 1;
  billDetail: any;
  selectedCompanyId = null;
  selectedVendorId = null;
  selectedPaymentStatus = '';
  billDateRange: any = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
  paymentStats: any = {};
  paymentStatCards = BILL_PAYMENT_STAT_CARDS;
  amountStatCards = BILL_AMOUNT_STAT_CARDS;
  companies: any;
  vendors: any;
  loadingId = null;
  syncType = null;
  selectedDropdownId: any;
  toggleActionButtons = false;

  numberOfRecords = NUMBER_OF_RECORDS;
  getPaymentStatus = getPaymentStatus;
  getPaymentStatusClass = getPaymentStatusClass;
  getAmountPaid = getAmountPaid;
  getRemainingBalance = getRemainingBalance;

  constructor(
    private companyService: CompanyService,
    private vendorService: VendorService,
    private billService: BillService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.selectedPaymentStatus = this.route.snapshot.queryParams['paymentStatus'] || '';
    this.getCompanies();
    this.initializeSearch();
    this.getAllBills(this.currentPage);
  }

  getAllBills(page: any) {
    const queryParams: any = {
      limit: this.numberOfRecords,
      page,
    }
    if (this.search) {
      queryParams.search = this.search.trim();
    }
    if (this.selectedCompanyId) {
      queryParams.company = this.selectedCompanyId;
    }
    if (this.selectedVendorId) {
      queryParams.vendor = this.selectedVendorId;
    }
    if (this.selectedPaymentStatus) {
      queryParams.paymentStatus = this.selectedPaymentStatus;
    }
    const dates = this.billDateQuery();
    if (dates.startDate) {
      queryParams.startDate = dates.startDate;
      queryParams.endDate = dates.endDate;
    }
    this.billService.getBills(queryParams)
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          if (data) {
            const { data: rows = [], overview = {} } = (data?.[0] || {}) as any;
            this.bills = rows;
            this.filteredBills = [...this.bills];
            this.totalPages = overview.totalPages;
            this.totalResults = overview.totalResults;
            this.paymentStats = overview;
          }
        },
        error: (err) => {
          sweetAlert('Error', err, 'error', 'Ok');
        }
      })
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getAllBills(page);
  }

  openDetailModal(bill: any) {
    this.billDetail = bill;
  }

  getCompanies() {
    this.companyService.getCompanies()
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.companies = data.results
        }
      })
  }

  onCompanyFilterChange() {
    this.selectedVendorId = null;
    this.vendors = null;
    this.currentPage = 1;
    if (this.selectedCompanyId) {
      this.getVendors();
    }
    this.getAllBills(this.currentPage);
  }

  onVendorFilterChange() {
    this.currentPage = 1;
    this.getAllBills(this.currentPage);
  }

  onBillDateChange(event: any) {
    // owl range emits incomplete [start, null] while picking — do not clobber the active range
    if (!event?.[0] && !event?.[1]) {
      this.billDateRange = null;
      this.currentPage = 1;
      this.getAllBills(this.currentPage);
      return;
    }
    if (!event?.[0] || !event?.[1]) {
      return;
    }
    this.billDateRange = event;
    this.currentPage = 1;
    this.getAllBills(this.currentPage);
  }

  billDateQuery() {
    if (!this.billDateRange?.[0] || !this.billDateRange?.[1]) {
      return {};
    }
    return {
      startDate: moment(this.billDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(this.billDateRange[1]).format('YYYY-MM-DD'),
    };
  }

  onPaymentStatusFilterChange() {
    this.currentPage = 1;
    this.getAllBills(this.currentPage);
  }

  getVendors() {
    this.vendorService.getVendors({
      limit: 9999,
      company: this.selectedCompanyId
    }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.vendors = (data.results || []).filter((vendor: any) => vendor.Id)
      }
    })
  }

  initializeSearch() {
    this.searchSubject
      .pipe(
        debounceTime(500),
        filter((search: any) => !search || search.length >= 3),
        untilDestroyed(this)
      ).subscribe(() => {
        this.currentPage = 1;
        this.getAllBills(this.currentPage);
      });
  }

  onSearchInput() {
    this.searchSubject.next(this.search);
  }

  async onSyncBill(bill: any, type: any) {
    const mPull = 'Are you sure you want to overwrite bill data with QuickBooks data?';
    const mPush = 'Are you sure you want to overwrite the QuickBooks data with bill data';
    const result = await sweetAlert('Alert', type === 'pull' ? mPull : mPush, 'warning', 'Yes', 'No');
    if (!result.value) return;

    this.loadingId = bill.id;
    this.syncType = type;
    if (type === 'pull') bill.pulling = true; else bill.pushing = true;
    this.closeActionMenu();
    this.billService.syncBill(bill.company.id, bill.id, { isSyncFrom: type === 'pull' }).subscribe({
      next: (data: any) => {
        this.getAllBills(this.currentPage);
        sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
        bill.pulling = false;
        bill.pushing = false;
        this.loadingId = null;
        this.syncType = null;
      },
      complete: () => {
        bill.pulling = false;
        bill.pushing = false;
        this.loadingId = null;
        this.syncType = null;
      }
    })
  }

  onSendEmail(companyId: any, billId: any) {
    this.closeActionMenu();
    this.billService.sendEmail(companyId, billId).subscribe({
      next: (data: any) => {
        sweetAlert('Success', data?.message || 'Email sent successfully', 'success', 'Ok');
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    });
  }

  async onDeleteBill(companyId: any, billId: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to delete this bill?', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.closeActionMenu(null);
    this.billService.deleteBill(companyId, billId).subscribe({
      next: () => {
        this.getAllBills(this.currentPage);
      },
      error: (err) => {
        sweetAlert("Error", err, "error", "Ok");
      }
    })
  }

  showActionButtons(id: any) {
    this.selectedDropdownId = id;
    this.toggleActionButtons = true;
  }

  closeActionMenu(_event?: any) {
    this.selectedDropdownId = null;
    this.toggleActionButtons = false;
  }

  openPayBill(billId: any) {
    this.router.navigateByUrl(`/bills/${billId}/pay`);
  }

}
